import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DocumentationChecklist = ({ isOpen, onClose, serviceType }) => {
  const [checkedDocs, setCheckedDocs] = useState({});

  const toggleDoc = (docId) => {
    setCheckedDocs(prev => ({
      ...prev,
      [docId]: !prev?.[docId]
    }));
  };

  const documentationCategories = [
    {
      category: 'Business Context',
      icon: 'Briefcase',
      color: 'bg-purple-500',
      items: [
        {
          id: 'business-objectives',
          title: 'Strategic Business Objectives',
          description: 'Company goals and how data can support them',
          priority: 'High',
          examples: ['Business plan excerpts', 'Strategic initiatives', 'Growth targets']
        },
        {
          id: 'pain-points',
          title: 'Current Pain Points & Challenges',
          description: 'Documented issues with current data processes',
          priority: 'High',
          examples: ['Problem statements', 'Bottleneck analysis', 'User feedback']
        },
        {
          id: 'use-cases',
          title: 'Key Use Cases',
          description: 'Specific scenarios where data insights are needed',
          priority: 'Medium',
          examples: ['Decision-making scenarios', 'Operational workflows', 'Customer journeys']
        },
        {
          id: 'success-metrics',
          title: 'Success Metrics & KPIs',
          description: 'How you currently measure business performance',
          priority: 'Medium',
          examples: ['KPI definitions', 'Performance metrics', 'Success criteria']
        }
      ]
    },
    {
      category: 'Compliance & Governance',
      icon: 'Shield',
      color: 'bg-red-500',
      items: [
        {
          id: 'compliance-requirements',
          title: 'Regulatory Compliance Requirements',
          description: 'Industry regulations and compliance obligations',
          priority: 'High',
          examples: ['GDPR documentation', 'Industry regulations', 'Compliance policies']
        },
        {
          id: 'data-policies',
          title: 'Existing Data Policies',
          description: 'Current data governance, security, and privacy policies',
          priority: 'Medium',
          examples: ['Data governance framework', 'Security policies', 'Access controls']
        },
        {
          id: 'audit-reports',
          title: 'Recent Audit Reports',
          description: 'Any recent data audits or assessments',
          priority: 'Low',
          examples: ['Audit findings', 'Risk assessments', 'Compliance reviews']
        }
      ]
    },
    {
      category: 'Current State Documentation',
      icon: 'FolderOpen',
      color: 'bg-[#002B45]',
      items: [
        {
          id: 'data-architecture',
          title: 'Data Architecture Diagrams',
          description: 'Current data flow, storage systems, and integration points',
          priority: 'High',
          examples: ['System architecture diagrams', 'Data flow charts', 'Integration maps']
        },
        {
          id: 'tech-stack',
          title: 'Technology Stack Overview',
          description: 'List of current data tools, platforms, and technologies in use',
          priority: 'High',
          examples: ['Database systems', 'Analytics platforms', 'ETL tools', 'Cloud services']
        },
        {
          id: 'data-inventory',
          title: 'Data Inventory',
          description: 'Catalog of data sources, types, and volumes',
          priority: 'Medium',
          examples: ['Data source list', 'Data volume metrics', 'Data types catalog']
        },
        {
          id: 'existing-reports',
          title: 'Existing Reports & Dashboards',
          description: 'Current reporting outputs and analytics dashboards',
          priority: 'Medium',
          examples: ['Sample reports', 'Dashboard screenshots', 'KPI definitions']
        }
      ]
    },
    {
      category: 'Organizational Information',
      icon: 'Users',
      color: 'bg-green-500',
      items: [
        {
          id: 'org-chart',
          title: 'Organizational Structure',
          description: 'Team structure and data-related roles',
          priority: 'Medium',
          examples: ['Org chart', 'Team responsibilities', 'Reporting lines']
        },
        {
          id: 'stakeholder-list',
          title: 'Key Stakeholder List',
          description: 'Decision-makers and data consumers in your organization',
          priority: 'High',
          examples: ['Stakeholder names & roles', 'Data needs by department', 'Decision authority']
        },
        {
          id: 'team-skills',
          title: 'Team Skills & Capabilities',
          description: 'Current data team composition and skill levels',
          priority: 'Low',
          examples: ['Team member profiles', 'Skill assessments', 'Training history']
        }
      ]
    },
    {
      category: 'Budget & Resources',
      icon: 'DollarSign',
      color: 'bg-yellow-500',
      items: [
        {
          id: 'budget-info',
          title: 'Budget Information',
          description: 'Current data-related spending and available budget',
          priority: 'Medium',
          examples: ['Current tool costs', 'Team budget', 'Investment capacity']
        },
        {
          id: 'timeline',
          title: 'Project Timeline & Constraints',
          description: 'Expected timelines and any critical deadlines',
          priority: 'Medium',
          examples: ['Project milestones', 'Business deadlines', 'Resource availability']
        }
      ]
    }
  ];

  // Calculate progress
  const totalDocs = documentationCategories?.reduce((sum, cat) => sum + cat?.items?.length, 0);
  const checkedCount = Object.values(checkedDocs)?.filter(Boolean)?.length;
  const progressPercentage = (checkedCount / totalDocs) * 100;

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'Low':
        return 'bg-green-100 text-green-700 border-green-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-white rounded-2xl shadow-premium max-w-5xl w-full max-h-[90vh] overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#002B45] to-[#003D5C] px-8 py-6 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center">
              <Icon name="FileText" size={24} className="text-[#1FB5A5]" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Documentation Checklist</h2>
              <p className="text-white/80 text-sm">Gather these materials before your consultation</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
          >
            <Icon name="X" size={20} className="text-white" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-8 py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Your Progress</span>
            <span className="text-sm font-bold text-[#1FB5A5]">
              {checkedCount} of {totalDocs} items gathered
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div
              className="bg-gradient-to-r from-[#1FB5A5] to-emerald-500 h-full transition-all duration-500"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>

        {/* Help Section - Don't Have Everything */}
        <div className="px-8 py-6 bg-gradient-to-r from-[#E6F0F5] to-[#D1F5F0] border-b border-[#002B45]/20">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-[#002B45] rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon name="Info" size={24} className="text-white" />
            </div>
            <div>
              <h4 className="font-bold text-[#002B45] mb-2">Don't Have Everything?</h4>
              <p className="text-sm text-gray-700 mb-3">
                Don't worry! This is a comprehensive list. Bring what you have, and we'll work together to identify what's most important for your specific situation. Even partial documentation helps us have a more productive conversation.
              </p>
              <div className="flex items-center space-x-2 text-sm text-[#002B45]">
                <Icon name="CheckCircle" size={16} />
                <span className="font-medium">We'll guide you through the rest during our consultation</span>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-200px)] px-8 py-6">
          <div className="space-y-8">
            {documentationCategories?.map((category, catIndex) => (
              <div key={catIndex} className="space-y-4">
                {/* Category Header */}
                <div className="flex items-center space-x-3 pb-3 border-b-2 border-gray-200">
                  <div className={`${category?.color} w-10 h-10 rounded-lg flex items-center justify-center`}>
                    <Icon name={category?.icon} size={20} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#002B45]">{category?.category}</h3>
                </div>

                {/* Category Items */}
                <div className="space-y-3">
                  {category?.items?.map((item) => (
                    <div
                      key={item?.id}
                      className={`border-2 rounded-xl p-4 transition-all duration-300 ${
                        checkedDocs?.[item?.id]
                          ? 'border-[#1FB5A5] bg-emerald-50'
                          : 'border-gray-200 hover:border-[#1FB5A5]/50'
                      }`}
                    >
                      <div className="flex items-start space-x-4">
                        {/* Checkbox */}
                        <button
                          onClick={() => toggleDoc(item?.id)}
                          className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-1 transition-all ${
                            checkedDocs?.[item?.id]
                              ? 'bg-[#1FB5A5] border-[#1FB5A5]'
                              : 'border-gray-300 hover:border-[#1FB5A5]'
                          }`}
                        >
                          {checkedDocs?.[item?.id] && (
                            <Icon name="Check" size={16} className="text-white" />
                          )}
                        </button>

                        {/* Content */}
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-2">
                            <h4
                              className={`font-semibold text-[#002B45] ${
                                checkedDocs?.[item?.id] ? 'line-through opacity-60' : ''
                              }`}
                            >
                              {item?.title}
                            </h4>
                            <span
                              className={`text-xs font-medium px-2 py-1 rounded border ${getPriorityColor(
                                item?.priority
                              )}`}
                            >
                              {item?.priority} Priority
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mb-3">{item?.description}</p>
                          
                          {/* Examples */}
                          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                            <p className="text-xs font-semibold text-gray-700 mb-2 flex items-center">
                              <Icon name="Lightbulb" size={14} className="mr-1 text-yellow-500" />
                              Examples:
                            </p>
                            <ul className="space-y-1">
                              {item?.examples?.map((example, idx) => (
                                <li key={idx} className="text-xs text-gray-600 flex items-start">
                                  <span className="text-[#1FB5A5] mr-2">•</span>
                                  {example}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-8 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            <Icon name="Download" size={16} className="inline mr-1" />
            <span>Download this checklist as PDF from Recommended Resources</span>
          </div>
          <Button onClick={onClose} className="bg-[#002B45] hover:bg-[#003D5C]">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DocumentationChecklist;